// src/pages/PatientOrderLabTestPage.jsx
// Route: /patient/lab-tests/order
// Patient browses the Thyrocare catalog, picks a test, enters details and places order.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { labTestAPI, walletAPI } from '../services/api';
import { payAndCreateLabTestOrder, payAndCreateLabTestOrderWithWallet } from '../utils/labTestPayment';
import { INDIAN_STATES, groupTests } from '../utils/labTestUtils';
import Icon from '../components/Icon';
import TestDetailsModal from '../components/features/labtest/TestDetailsModal';

function today() { return new Date().toISOString().slice(0, 10); }
function maxDate() {
  const d = new Date(); d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

// 15 Jul 2026: Thyrocare's create-order API requires contactNumber as
// '+YY-XXXXXXXXXX' (country code + 10-digit number) — confirmed via the
// actual 400 error: "contactNumber must be in format '+YY-XXXXXXXXXX'".
// patient.phone is stored as a plain digit string with no country code or
// formatting. India-only platform, so +91 is hardcoded here rather than
// made configurable — same assumption already made elsewhere in this
// codebase (e.g. IST timezone hardcoded in appointment booking).
function toThyrocareContactNumber(rawPhone) {
  const digitsOnly = String(rawPhone || '').replace(/\D/g, '');
  const last10 = digitsOnly.slice(-10);
  if (last10.length !== 10) return ''; // fail-safe — let Thyrocare's own validation catch it
  return `+91-${last10}`;
}

const STEPS = ['Choose Test', 'Select Slot', 'Confirm Order'];

export default function PatientOrderLabTestPage() {
  const { patient, profile } = usePatient();
  const navigate    = useNavigate();

  const [step, setStep]         = useState(0);
  const [catalog, setCatalog]   = useState([]);
  const [slots, setSlots]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [success, setSuccess]           = useState(null);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');

  const [selectedTest, setSelectedTest] = useState(null);
  const [pincode, setPincode]           = useState('');
  const [date, setDate]                 = useState(today());
  const [selectedSlot, setSelectedSlot] = useState('');
  const [address, setAddress]           = useState({ addressLine1: '', city: '', state: INDIAN_STATES[0], pincode: '' });
  const [walletBalanceMinorUnits, setWalletBalanceMinorUnits] = useState(0);

  // "View tests" modal state — a per-panel test breakdown this page never
  // fetched before (it only ever had the total count, noOfTestsIncluded).
  // Fetched lazily via the same labTestAPI.getPackageDetails(skuId) call the
  // Navigator/Doctor booking flow (PlanCatalogStep.jsx) uses — same API,
  // independent state, so nothing here touches that file or its behaviour.
  const [modalTest, setModalTest]                     = useState(null);
  const [detailsByTestId, setDetailsByTestId]         = useState({});
  const [loadingTestId, setLoadingTestId]             = useState(null);
  const [detailsErrorByTestId, setDetailsErrorByTestId] = useState({});

  function loadTestDetails(test) {
    setLoadingTestId(test.id);
    setDetailsErrorByTestId(prev => ({ ...prev, [test.id]: null }));
    labTestAPI.getPackageDetails(test.id)
      .then(res => {
        setDetailsByTestId(prev => ({ ...prev, [test.id]: res.data?.testsIncluded || [] }));
      })
      .catch(() => {
        setDetailsErrorByTestId(prev => ({ ...prev, [test.id]: true }));
      })
      .finally(() => setLoadingTestId(null));
  }

  function openTestDetails(e, test) {
    e.stopPropagation(); // don't also trigger the card's onClick (select test)
    setModalTest(test);
    if (!detailsByTestId[test.id] && loadingTestId !== test.id) loadTestDetails(test);
  }

  const modalGroups = modalTest
    ? groupTests(detailsByTestId[modalTest.id])
    : [];

  // 27 Jul 2026: new — powers the "Pay with wallet" option below.
  useEffect(() => {
    if (!patient?.id) return;
    walletAPI.getBalance(patient.id)
      .then(res => setWalletBalanceMinorUnits(res.data.balanceMinorUnits || 0))
      .catch(() => { /* silent — wallet option just won't show if this fails */ });
  }, [patient?.id]);

  // Load catalog
  // 15 Jul 2026: was calling labTestAPI.catalogList() — the raw Thyrocare
  // catalog browse endpoint, showing every synced SKU (AAROGYAM 1.1/1.2/1.3/
  // 1.4/"1 WITH UTSH") with lab-partner names and pricing instead of Previu's
  // own T1/T2 panels. This is the SAME bug already fixed once in
  // useLabTestBooking.js — but that hook turns out to belong to a different
  // flow entirely; THIS page never used it, so the earlier fix never reached
  // here. Confirmed via Network tab: this page's only lab-test request was
  // still GET /labtest/catalog/list, not POST /labtest/panel-pricing.
  //
  // Switched to labTestAPI.panelPricing() — same Previu-branded endpoint the
  // chat's get_panel_pricing tool and useLabTestBooking.js both use — and
  // adapted the response into the exact shape this component's render code
  // already expects (test.id/name/rate/flags/noOfTestsIncluded/type), so no
  // changes needed below this effect.
  //
  // 16 Sep 2026: ONCOVERYX retired — replaced by two gender-specific Tumour
  // Panels (TUMOUR_MALE / TUMOUR_FEMALE, see PanelDefinitionService). Which
  // one gets requested depends on profile.gender (NOT patient.gender — the
  // latter never carries gender at all, see PatientLayout.js's
  // MissingGenderBanner comment). Deliberately does NOT default an
  // unknown/missing gender to either panel — guessing would show the wrong
  // one — so a patient without gender on file sees T1/T2 only, same as
  // before gender was on file at all. T3/360 stays excluded — it requires
  // Dr. Ajay clinical review before ordering (explicit warning on every
  // panel-pricing response) and is genuinely clinically triggered.
  useEffect(() => {
    if (!patient?.id) return;
    const gender = String(profile?.gender || '').toUpperCase();
    const requestedPanelCodes = ['T1', 'T2'];
    if (gender === 'MALE')        requestedPanelCodes.push('TUMOUR_MALE');
    else if (gender === 'FEMALE') requestedPanelCodes.push('TUMOUR_FEMALE');
    labTestAPI.panelPricing({ patientId: patient.id, panels: requestedPanelCodes })
      .then(res => {
        const panels = Array.isArray(res.data?.panels) ? res.data.panels : [];
        const adapted = panels
          .filter(p => p.skuId) // drop any panel Thyrocare lookup failed for
          .map(p => ({
            id:   p.skuId,
            // 15 Jul 2026 (fix): p.name from panel-pricing already includes
            // the code — PanelDefinitionService's registry stores it as
            // "T1 — Essential Panel" / "T2 — Standard Panel" directly.
            // Previously prepended p.code again here, producing the visible
            // "T2 — T2 — Standard Panel" duplication.
            name: p.name,
            // 15 Jul 2026: panelCode kept separately (not parsed out of
            // `name`) — needed by the new payment flow's
            // createLabTestPaymentOrder({ patientId, panelCode }) call,
            // which resolves the amount server-side from this code.
            panelCode: p.code,
            type: p.skuType,
            rate: { sellingPrice: p.price },
            noOfTestsIncluded: p.testsIncluded,
            flags: { isFastingRequired: p.fasting },
          }));
        setCatalog(adapted);
      })
      .catch(() => setError('Could not load test catalog.'))
      .finally(() => setLoading(false));
  }, [patient?.id, profile?.gender]);

  // Load slots when test + date + pincode set
  async function searchSlots() {
    if (!selectedTest || !date || !pincode) return;
    setLoadingSlots(true);
    setError('');
    // Clear any slot picked in a previous search. Real availability shrinks
    // as the day goes on (an evening slot search re-run later returns fewer,
    // later options), so a stale selectedSlot from an earlier search can
    // point at a time that's no longer in the new results — "Continue to
    // Confirm" only checked truthiness, not membership in the current list,
    // so it silently carried that expired slot through to order placement
    // and Thyrocare rejected it with "Given slot is not available."
    setSelectedSlot('');
    try {
      const res = await labTestAPI.slotsSearch({
        appointmentDate: date,
        pincode,
        patients: [{
          name:    `${patient.firstName} ${patient.lastName}`,
          gender:  'MALE',
          age:     30,
          ageType: 'YEAR',
          items:   [{ id: selectedTest.id, type: selectedTest.type ?? 'SSKU', name: selectedTest.name }],
        }],
      });
      const slotsData = res.data?.slots ?? res.data ?? [];
      setSlots(Array.isArray(slotsData) ? slotsData : []);
      setStep(1);
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Could not load slots. Please try a different date or pincode.');
    } finally {
      setLoadingSlots(false);
    }
  }

  async function placeOrder(usingWallet) {
    setSubmitting(true);
    setError('');
    try {
      // 15 Jul 2026: was calling labTestAPI.createOrder() directly with
      // paymentDetails.payType hardcoded to POSTPAID and no actual payment
      // step anywhere in this flow. Now gated on a verified Razorpay
      // payment first — see src/utils/labTestPayment.js. The order payload
      // itself is UNCHANGED; only WHEN it gets sent to Thyrocare changed
      // (after payment, not immediately).
      const orderPayload = {
        email:         patient.email || '',
        contactNumber: toThyrocareContactNumber(patient.phone),
        appointment: { date, startTime: selectedSlot, timeZone: 'IST' },
        address: {
          addressLine1: address.addressLine1 || 'Home Collection',
          city:         address.city || 'Bangalore',
          state:        address.state || 'Karnataka',
          country:      'India',
          pincode:      pincode || address.pincode,
        },
        origin: { platform: 'All', appId: 'PREVIU', portalType: 'B2C',
          enteredBy: 'patient', source: 'B2C-CREATE-ORDER-API' },
        paymentDetails: { payType: 'POSTPAID' }, // Thyrocare's own field — unrelated to Previu's separate Razorpay collection below; POSTPAID is the only value ever confirmed working against this API, left unchanged rather than guessing at an unverified alternative
        referredBy: { doctorId: '', doctorName: '' },
        attributes: { remarks: '', phleboNotes: '', campId: null,
          isReportHardCopyRequired: false,
          refOrderNo: `PRV-${patient.id?.slice(-8)}-${String(Date.now()).slice(-6)}`,
          collectionType: 'HOME_COLLECTION', alertMessage: [''] },
        config: { communication: { shareReport: true, shareReceipt: true,
          shareModes: { whatsapp: true, email: true } } },
        orderOptions: { isPdpcOrder: false },
        price: { discounts: [{ type: 'HOME_VISIT_DISCOUNT', amount: '' }],
          incentivePasson: { type: 'FLAT', value: '0' } },
        patients: [{
          name:          `${patient.firstName} ${patient.lastName}`,
          gender:        'MALE',
          age:           '30',
          ageType:       'YEAR',
          contactNumber: toThyrocareContactNumber(patient.phone),
          email:         patient.email || '',
          attributes:    { ulcUniqueCode: '', patientAddress: 'Home', externalPatientId: '' },
          documents:     [{ attributes: '{}', documentPath: '', documentType: '' }],
          items: [{
            id:     selectedTest.id,
            type:   selectedTest.type ?? 'SSKU',
            name:   selectedTest.name,
            origin: { enteredBy: 'patient', platform: 'ALL' },
          }],
        }],
      };

      // 27 Jul 2026: routes to the wallet-combined checkout when the patient
      // picked "Pay with wallet" — same two functions used by the chat
      // ordering flow (src/utils/labTestPayment.js), not a separate
      // implementation.
      const payFn = usingWallet ? payAndCreateLabTestOrderWithWallet : payAndCreateLabTestOrder;
      const result = await payFn({
        patientId:    patient.id,
        panelCode:    selectedTest.panelCode,
        panelName:    selectedTest.name,
        patientName:  `${patient.firstName} ${patient.lastName}`,
        patientEmail: patient.email,
        patientPhone: patient.phone,
        orderPayload,
      });

      if (result.success) {
        setSuccess(result.order);
        return;
      }

      // Distinct wording per failure point — "click to retry" only makes
      // sense for cancelled/failed payment attempts, not for the
      // order-creation-after-payment case, which needs support contact
      // instead of a naive retry (retrying could mean paying twice).
      if (result.reason === 'payment_cancelled') {
        setError('Payment was not completed. Click "Pay & Place Order" to try again.');
      } else if (result.reason === 'payment_failed') {
        setError(`Payment failed: ${result.message || 'please try again.'}`);
      } else if (result.reason === 'order_creation_failed') {
        setError(result.message);
      } else {
        setError(result.message || 'Could not place order. Please try again.');
      }
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Could not place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const filteredCatalog = catalog.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase())
  );

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    border: '1.5px solid var(--border)', borderRadius: 'var(--radius-md)',
    padding: '10px 14px', fontSize: 14, fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)', background: 'var(--bg-subtle)', outline: 'none',
  };

  if (success) return (
    <PatientAppShell breadcrumbs={[
      { label: 'My Lab Tests', to: '/patient/lab-tests' },
      { label: 'Order Placed' },
    ]}>
      <div style={{ maxWidth: 480, margin: '60px auto', textAlign: 'center' }}>
        <div className="empty-icon empty-icon--badge" aria-hidden="true"
             style={{ width: 72, height: 72, marginBottom: 16 }}>
          <Icon name="check" size={30} />
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22,
          color: 'var(--text-primary)', marginBottom: 8 }}>
          Order Placed!
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 8 }}>
          Your order <strong>{success.orderNo ?? success.orderId}</strong> has been confirmed.
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 28 }}>
          A technician will collect your sample at home on <strong>{date}</strong> at <strong>{selectedSlot}</strong>.
        </p>
        <button
          onClick={() => navigate('/patient/lab-tests')}
          style={{
            padding: '10px 24px', borderRadius: 'var(--radius-md)',
            background: 'var(--brand-accent)', border: 'none',
            color: 'var(--text-on-brand-dark)', fontWeight: 600, fontSize: 14,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          View My Orders
        </button>
      </div>
    </PatientAppShell>
  );

  return (
    <PatientAppShell breadcrumbs={[
      { label: 'My Lab Tests', to: '/patient/lab-tests' },
      { label: 'Order Lab Test' },
    ]}>
      <div style={{ maxWidth: 640 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700,
          color: 'var(--text-primary)', marginBottom: 6 }}>
          Order a Lab Test
        </h1>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderBottom: '1px solid var(--border)' }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{
              padding: '10px 16px', fontSize: 13, fontWeight: step === i ? 600 : 400,
              color: step === i ? 'var(--brand-accent)' : 'var(--text-muted)',
              borderBottom: step === i ? '2px solid var(--brand-accent)' : '2px solid transparent',
              marginBottom: -1, cursor: i < step ? 'pointer' : 'default',
            }} onClick={() => i < step && setStep(i)}>
              {i + 1}. {s}
            </div>
          ))}
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: 'var(--red-bg)', borderRadius: 8,
            color: 'var(--red)', fontSize: 13, marginBottom: 20 }}>
            {error}
          </div>
        )}

        {/* Step 0: Choose test */}
        {step === 0 && (
          <div>
            <input
              type="text" placeholder="Search tests…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, marginBottom: 16 }}
            />
            {loading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading catalog…</div>
            ) : (
              // 4px padding keeps the cards' focus outline inside this
              // scroll container instead of clipped by it.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 360, overflowY: 'auto', padding: 4 }}>
                {filteredCatalog.map(test => {
                  const isSelected = selectedTest?.id === test.id;
                  return (
                    <div
                      key={test.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      className={`select-card${isSelected ? ' select-card--selected' : ''}`}
                      onClick={() => setSelectedTest(test)}
                      onKeyDown={(e) => {
                        // Only keys pressed on the card itself — Enter/Space on
                        // the nested "View tests" button bubble up here too.
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedTest(test);
                        }
                      }}
                    >
                      <div className="select-card__head">
                        <div className="select-card__title">{test.name}</div>
                        <button
                          type="button"
                          className="select-card__action"
                          onClick={(e) => openTestDetails(e, test)}
                        >
                          View tests →
                        </button>
                      </div>
                      <div className="select-card__meta">
                        {test.noOfTestsIncluded} tests ·{' '}
                        ₹{test.rate?.sellingPrice ?? test.rate?.listingPrice}
                        {test.flags?.isFastingRequired ? ' · Fasting required' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedTest && (
              <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)',
                      display: 'block', marginBottom: 6 }}>
                      Collection Date
                    </label>
                    <input type="date" value={date} min={today()} max={maxDate()}
                      onChange={e => setDate(e.target.value)} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)',
                      display: 'block', marginBottom: 6 }}>
                      Pincode
                    </label>
                    <input type="text" placeholder="e.g. 560078" value={pincode}
                      onChange={e => setPincode(e.target.value)} style={inputStyle} />
                  </div>
                </div>
                <button type="button" onClick={searchSlots}
                  disabled={!pincode || loadingSlots}
                  style={{
                    padding: '11px 24px', borderRadius: 'var(--radius-md)',
                    background: !pincode || loadingSlots ? 'var(--text-muted)' : 'var(--brand-accent)',
                    border: 'none', color: 'var(--text-on-brand-dark)', fontWeight: 600, fontSize: 14,
                    cursor: !pincode || loadingSlots ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                  }}>
                  {loadingSlots ? 'Searching slots…' : 'Find Available Slots →'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Select slot */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: 16, padding: '12px 16px',
              background: 'var(--brand-light)', borderRadius: 8,
              fontSize: 13, color: 'var(--amber-text)' }}>
              <strong>{selectedTest?.name}</strong> · ₹{selectedTest?.rate?.sellingPrice}
              {' · '}{date}
            </div>

            {slots.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 14,
                padding: '20px 0', textAlign: 'center' }}>
                No slots found. Try a different date or pincode.
                <button type="button" onClick={() => setStep(0)}
                  style={{ display: 'block', margin: '12px auto 0',
                    background: 'none', border: 'none', color: 'var(--brand-accent)',
                    cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                  ← Go back
                </button>
              </div>
            ) : (
              <>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)',
                  display: 'block', marginBottom: 10 }}>
                  Available Collection Slots
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                  {slots.map((slot, i) => {
                    const label = typeof slot === 'string' ? slot : slot.startTime ?? JSON.stringify(slot);
                    return (
                      <button key={i} type="button" onClick={() => setSelectedSlot(label)}
                        style={{
                          padding: '8px 16px', borderRadius: 20, fontSize: 13,
                          fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                          border: selectedSlot === label
                            ? '2px solid var(--brand-accent)' : '1.5px solid var(--border)',
                          background: selectedSlot === label ? 'var(--brand-accent)' : 'var(--bg-surface)',
                          color: selectedSlot === label ? 'var(--text-on-brand-dark)' : 'var(--text-primary)',
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Address */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 500,
                    color: 'var(--text-secondary)' }}>
                    Collection Address
                  </label>
                  <input placeholder="Address line 1" value={address.addressLine1}
                    onChange={e => setAddress(a => ({ ...a, addressLine1: e.target.value }))}
                    style={inputStyle} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input placeholder="City" value={address.city}
                      onChange={e => setAddress(a => ({ ...a, city: e.target.value }))}
                      style={{ ...inputStyle, flex: 1 }} />
                    {/* Dropdown, not free text — same INDIAN_STATES list and
                        rationale as AddressSlotStep.jsx (navigator's lab-test
                        flow) and AddressPage.js: bounded set, so a dropdown
                        rules out typos/inconsistent casing a state field
                        otherwise invites ("karnataka" vs "Karnataka"). City
                        stays free text — there's no equivalent bounded city
                        list anywhere in this codebase to draw options from. */}
                    <select value={address.state}
                      onChange={e => setAddress(a => ({ ...a, state: e.target.value }))}
                      style={{ ...inputStyle, flex: 1 }}>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <button type="button" onClick={() => setStep(2)}
                  disabled={!selectedSlot}
                  style={{
                    padding: '11px 24px', borderRadius: 'var(--radius-md)',
                    background: !selectedSlot ? 'var(--text-muted)' : 'var(--brand-accent)',
                    border: 'none', color: 'var(--text-on-brand-dark)', fontWeight: 600, fontSize: 14,
                    cursor: !selectedSlot ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}>
                  Continue to Confirm →
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 2 && (
          <div>
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '20px 24px',
              display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
                marginBottom: 4 }}>
                Order Summary
              </h3>
              {[
                ['Test',            selectedTest?.name],
                ['Date',            date],
                ['Time',            selectedSlot],
                ['Pincode',         pincode],
                ['Amount',          `₹${selectedTest?.rate?.sellingPrice}`],
                ['Payment',         'Pay now via Razorpay'],
                ['Collection type', 'Home Collection'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between',
                  fontSize: 14, borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{v}</span>
                </div>
              ))}
            </div>

            <button type="button" onClick={() => placeOrder(false)} disabled={submitting}
              style={{
                padding: '12px 28px', borderRadius: 'var(--radius-md)',
                background: submitting ? 'var(--text-muted)' : 'var(--brand-accent)',
                border: 'none', color: 'var(--text-on-brand-dark)', fontWeight: 600, fontSize: 15,
                cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}>
              {submitting ? 'Processing…' : 'Pay & Place Order'}
            </button>

            {/* 27 Jul 2026: new — wallet payment option, only shown once
                there's an actual balance to offer. Mirrors the chat card's
                approach (LabTestPaymentCard.jsx). */}
            {walletBalanceMinorUnits > 0 && (
              <button type="button" onClick={() => placeOrder(true)} disabled={submitting}
                className="btn btn-secondary"
                style={{ marginLeft: 12 }}>
                👛 Pay with wallet (₹{(walletBalanceMinorUnits / 100).toLocaleString('en-IN')} available)
              </button>
            )}
          </div>
        )}
      </div>

      {modalTest && (
        <TestDetailsModal
          test={modalTest}
          groups={modalGroups}
          loading={loadingTestId === modalTest.id}
          failed={!!detailsErrorByTestId[modalTest.id]}
          onRetry={() => loadTestDetails(modalTest)}
          onClose={() => setModalTest(null)}
        />
      )}
    </PatientAppShell>
  );
}
